import { memo, useRef, useMemo } from 'react';
import AssistantButton from './Button';
import AssistantDashboard from './Dashboard';
import { AssistantProvider, useAssistant } from './providers/Provider';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers/MytaverseEventProvider';
import { useUserName } from './providers/UserName';
import { env } from 'env';

const { REACT_APP_ENABLE_ASSISTANT_ON_EVENTS_PAGE } = env;

const AssistantWrapper = memo(
  ({ isSelectEventPage }: { isSelectEventPage: boolean }) => {
    const { isAIEnabledForEvent } = useAssistant();

    if (
      (REACT_APP_ENABLE_ASSISTANT_ON_EVENTS_PAGE && isSelectEventPage) ||
      (!isSelectEventPage && isAIEnabledForEvent())
    ) {
      return (
        <>
          <AssistantButton isLargeButton={isSelectEventPage} />
          <AssistantDashboard />
        </>
      );
    }

    return null;
  },
);

const Assistant = ({ isSelectEventPage }: { isSelectEventPage?: boolean }) => {
  const mytaverseEvent = useMytaverseEvent();
  const mytaverseEventRef = useRef(mytaverseEvent ?? {});
  mytaverseEventRef.current = mytaverseEvent ?? {};

  const { sendJSONMessageToWebSocket } = useMytaverse();
  const sendWSRef = useRef(sendJSONMessageToWebSocket);
  sendWSRef.current = sendJSONMessageToWebSocket;

  const userName = useUserName();

  const rawRegion = mytaverseEvent?.currentRegion?.region;
  // Sticky ref: only update when rawRegion is non-null so transient nulls
  // (e.g. from camera-sharing ParticipantPosition messages) don't change the
  // prop and break memo / trigger the region-change effect in the provider.
  const stickyRegionRef = useRef<string | undefined>(rawRegion);
  if (rawRegion) {
    stickyRegionRef.current = rawRegion;
  }
  const currentRegionName = stickyRegionRef.current;

  const children = useMemo(
    () => <AssistantWrapper isSelectEventPage={isSelectEventPage || false} />,
    [isSelectEventPage],
  );

  return (
    <AssistantProvider
      currentRegionName={currentRegionName}
      userName={userName}
      mytaverseEventRef={mytaverseEventRef}
      sendWSRef={sendWSRef}
    >
      {children}
    </AssistantProvider>
  );
};

export default Assistant;
